/**
 * Subnav scripts.
 * 
 * TODO: Consider how to resolve the jumping scroll trigger bug.
 */

/** Dependencies -------------------------------------------- */

// External

import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin( ScrollTrigger )

// Design system

import { setupEventName as smoothScrollSetupEventName } from '../smooth-scroll'
import { getCssVar } from '../helpers'

import { deriveBackgroundColorContext } from '../blocks/helpers'

/** The beef ------------------------------------------------ */

/**
 * Subnav mixin.
 * Provides subnav functionality to single element.
 */
export let mixin = ( { node = null, config = {} } = {} ) => {

    if ( ! node ) return false

    let state = {
        node,
        config,
		sibling                       : null,
		siblingBackgroundColorContext : null,
		items                         : null,
		links                         : null,
		select                        : null,
		anchorSelectors               : null,
		anchors                       : null,
		colorTriggers                 : null,
		isSticky                      : false
    }

    let module = {

		/** Setup ------------------------------------------- */
        
        init() {

			state.items = node.querySelectorAll( `.${state.config.className}-item` )
			state.links = node.querySelectorAll( `.${state.config.className}-item__link` )

			if ( ! state.links.length ) return

			// Get the adjoining element to determine what the background color should be at the jump.
            // This is either going to be a section or the footer.
            // When it's the footer, we have to back the query out and then continue, since the footer is outside of the main element.

            state.sibling = state.node.nextElementSibling || state.node.parentElement.parentElement.parentElement.nextElementSibling

            // And when that doesn't work, something is probably wrong and we bail

            if ( ! state.sibling ) return

			state.siblingBackgroundColorContext = deriveBackgroundColorContext( state.sibling )
			state.node.classList.add( `has-background-color-${state.siblingBackgroundColorContext.color}` )

			state.anchorLinks = Array.from( state.links ).filter( ( link ) => link.getAttribute( 'href' ).trim().indexOf( '#' ) === 0 )
			state.select = node.querySelector( `.${state.config.className}__select` )
			state.anchorSelectors = state.anchorLinks.map( ( link ) => link.getAttribute( 'href' ).trim() )
			state.anchors = document.querySelectorAll( state.anchorSelectors.join( ',' ) )
			state.colorTriggers = document.querySelectorAll( state.config.colorTriggerSelector )

			this.setEventBindings()			
        },

		setEventBindings() {

			state.anchorLinks.map( ( anchorLink ) => anchorLink.addEventListener( 'click', this.handleAnchorLinkClick.bind( this ) ) )
			state.select.addEventListener( 'change', this.handleSelectChange.bind( this ) )

			window.addEventListener( smoothScrollSetupEventName, this.handleSmoothScrollSetup.bind( this ) )
		},

		/** Main -------------------------------------------- */

		setAnchorTriggers() {
	
			for ( let [ iAnchor, anchor ] of state.anchors.entries() ) {
	
				ScrollTrigger.create(
					{
						trigger     : anchor,
						start       : () => `top ${this.getScrollOffset()}px`,
						end         : () => `+=${this.getAnchorHeight( iAnchor )}`,
						onEnter     : ( { trigger } ) => this.handleAnchorEnter( trigger ),
						onEnterBack : ( { trigger } ) => this.handleAnchorEnter( trigger ),
						onLeave     : iAnchor === state.anchors.length - 1 ? () => this.setSelect( 0 ) : null,
						onLeaveBack : iAnchor ? null : () => this.setSelect( 0 ),
						toggleClass : {
							targets   : Array.from( state.items )[iAnchor],
							className : state.config.activeClass
						}
					}
				)
			}
		},
	
		scrollTo( anchorSelector ) {
			gsap.to(
				window,
				{
					scrollTo : { y: anchorSelector, offsetY: this.getScrollOffset() + state.config.scrollOffsetGoose },
					duration : state.config.scrollDuration
				}
			)
		},

		/** Animations -------------------------------------- */

		setAnimations() {

			// Sticky magic

			ScrollTrigger.create(
				{
					trigger    : state.node,
					start      : () => `top ${this.getStickyOffset()}px`,
					end        : 'max',
					pin        : true,
					pinSpacing : false,
					onToggle   : ( scrollTrigger ) => {
						state.isSticky = scrollTrigger.isActive
						state.node.classList[scrollTrigger.isActive ? 'remove' : 'add']( `has-background-color-${state.siblingBackgroundColorContext.color}` )
					}
				}
			)

			// Color-changing magic

			Array.from( state.colorTriggers ).map(

				( colorTrigger ) => {

					ScrollTrigger.create(
						{
							trigger  : colorTrigger,
							start    : () => `top ${this.getBottomOffsetWhileSticky() + state.config.colorTriggerOffset}px`,
							end      : () => `bottom ${this.getBottomOffsetWhileSticky() + state.config.colorTriggerOffset}px`,
							onToggle : ( scrollTrigger ) => {

								// We only want to add the bg classes if the subnav is sticky
								// That'd be the only reason it's change color with respect to scroll
								if ( state.isSticky ) {
									const className = `has-background-color-${deriveBackgroundColorContext( colorTrigger ).color}`
									state.node.classList[scrollTrigger.isActive ? 'add' : 'remove']( className )
								}
							},
						}
					)
				}
			)
		},

		/** Event handlers ---------------------------------- */

		handleSmoothScrollSetup() {
			this.setAnchorTriggers()
			this.setAnimations()
		},

		handleAnchorLinkClick( e ) {
			e.preventDefault()
			this.scrollTo( e.target.getAttribute( 'href' ) )
		},

		handleSelectChange( e ) {
			const val = e.target.value
			if ( val ) this.scrollTo( val )
		},

		handleAnchorEnter( anchor ) {
			this.setSelect( `#${anchor.getAttribute( 'id' )}` );
		},

		/** Utilities -------------------------------------- */

		getStickyOffset() {

			const stickyOffsetVars = state.config.stickyOffsetVars.reduce(
				( a, v ) => a + parseInt( getCssVar( v ) || 0 ),
				0
			)

			const stickyOffsetSelectors = state.config.stickyOffsetSelectors.reduce(
				( a, s ) => {
					const closestBanner = state.node.parentElement.querySelector( s ) || state.node.parentElement.parentElement.querySelector( s )
					return closestBanner
						? a + parseInt( closestBanner.clientHeight )
						: 0
				},
				0
			)

			return stickyOffsetVars + stickyOffsetSelectors
		},

		getBottomOffsetWhileSticky() {
			return this.getStickyOffset() + state.node.getBoundingClientRect().height
		},

		getScrollOffset() {
			return this.getBottomOffsetWhileSticky() + state.config.colorTriggerOffset + state.config.scrollOffset
		},

		getAnchorHeight( iAnchor ) {

			const anchor = state.anchors.item( iAnchor )
			const anchorRect = anchor.getBoundingClientRect()
	
			let bottomBound;
	
			if ( iAnchor === state.anchors.length - 1 ) {
				bottomBound = anchorRect.bottom
			}
			else {
				const nextAnchor = state.anchors.item( iAnchor + 1 )
				const nextAnchorRect = nextAnchor.getBoundingClientRect()
				bottomBound = nextAnchorRect.top
			}
	
			const height = Math.abs( bottomBound - anchorRect.top )
			return height
		},

		setSelect( val ) {
			state.select.value = val
		},

    }

    return module.init()
};

/**
 * Subnav module.
 * Provides an interface to apply subnav functionality to all elements matching a selector.
 */
export default {

    init( { className, config = {} } ) {

        let selected = document.querySelectorAll( `.${className}` )

        if ( ! selected.length ) return

        const defaultConfig = {
			activeClass           : 'is-active',
			colorTriggerOffset    : 0,
			colorTriggerSelector  : 'section',
			scrollOffset          : 0,
			scrollOffsetGoose     : -4,
			scrollDuration        : 1,
            stickyOffsetVars      : ['bannerOffset'],
			stickyOffsetSelectors : ['.c-banner'],
        }

        return Array.from( selected ).map(
            ( node, index ) => mixin( { node, config: { ...defaultConfig, ...config, className } } )
        )
    }
}
